import React, { useEffect, useRef, useState } from 'react'

import {
  Button,
  FormHelperText,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'

import ImageIcon from '@mui/icons-material/Image'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import LoopIcon from '@mui/icons-material/Loop'

import utils from './FileUpload.Utils'
import Utils from '../../resources/Utils'

import {
  AlignCenter,
  AlignContent,
  Content,
  ContentError,
  DeleteButton,
  FileContainer,
  Image,
  ReplaceButton,
  UploadIcon,
  UploadImage,
  UploadImageDrag,
} from '../../styles/fileUploaderStyles'

const FileUploader = ({ file, disabled, ...props }) => {
  const initialState = {
    id: '',
    types: '',
    typesAccepted: [],
    file: null,
    open: false,
    drag: false,
  }

  const [state, setState] = useState({ ...initialState })
  const [dragCounter, setDragCounter] = useState(0)

  const fileFieldDocument = useRef(null)
  const dragField = useRef(null)

  const ContainerWrapper = props.error ? ContentError : Content
  const UploadWrapper = state.drag ? UploadImageDrag : UploadImage

  const handleAddFile = async (files) => {
    try {
      let file = files[0]

      if (file.type.includes('image')) {
        let reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = function (e) {
          let image = new window.Image()
          image.src = e.target?.result
          image.onload = () => {
            if (typeof reader.result === 'string') {
              file = {
                fileName: file.name,
                type: file.type,
                size: file.size,
                width: image.width,
                height: image.height,
                url: e.target?.result,
              }
              props.setFile(file)
              props.handleErrorFile && props.handleErrorFile()
            }
          }
        }
        reader.onerror = function (event) {
          throw new Error(event.target?.error?.message)
        }
      } else {
        const reader = new FileReader()
        reader.readAsArrayBuffer(file)
        reader.onload = (e) => {
          const buffer = e.target?.result
          const newBAse = Utils.arrayBufferToBase64(buffer, file.type)

          file = {
            fileName: file.name,
            type: file.type,
            size: file.size,
            url: newBAse,
          }
          props.setFile(file)
          props.handleErrorFile && props.handleErrorFile()
        }
      }
    } catch (error) {
      console.log('[Error filereader]', error)
    }
  }

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      id: utils.generateId(),
    }))
    handleRender()

    const div = dragField.current
    div?.addEventListener('dragenter', handleDragIn)
    div?.addEventListener('dragleave', handleDragOut)
    div?.addEventListener('dragover', handleDrag)
    div?.addEventListener('drop', handleDrop)

    return () => {
      div?.removeEventListener('dragenter', handleDragIn)
      div?.removeEventListener('dragleave', handleDragOut)
      div?.removeEventListener('dragover', handleDrag)
      div?.removeEventListener('drop', handleDrop)
    }
  }, [state.open])

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDragIn = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragCounter((count) => count + 1)
    if (e.dataTransfer?.items && e.dataTransfer.items.length > 0) {
      setState((prev) => ({ ...prev, drag: true }))
    }
  }

  const handleDragOut = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragCounter((count) => {
      const next = count - 1
      if (next === 0) {
        setState((prev) => ({ ...prev, drag: false }))
      }
      return next
    })
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setState((prev) => ({ ...prev, drag: false }))
    if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
      handleAddFile(e.dataTransfer.files)
      e.dataTransfer.clearData()
      setDragCounter(0)
    }
  }

  const handleRender = () => {
    const newTypes = utils.inputTypesAllowed(props.typesAccepted)
    setState((prevState) => ({ ...prevState, types: newTypes }))
  }

  const handleRenderImage = () => {
    if (file.type.includes('image')) {
      return (
        <Tooltip title="Show image">
          <Button disabled={disabled} fullWidth>
            <Image src={file.url} />
          </Button>
        </Tooltip>
      )
    } else if (file.type === 'application/pdf') {
      return (
        <AlignCenter>
          <Stack>
            <iframe src={file.url} style={{ width: '100%' }} />
          </Stack>
        </AlignCenter>
      )
    }
  }

  return (
    <>
      <ContainerWrapper>
        <input
          id={state.id}
          name={state.id}
          type="file"
          style={{ display: 'none' }}
          accept={state.types || 'image/png, image/jpeg'}
          onChange={(e) => handleAddFile(e.target.files)}
          disabled={disabled}
          ref={fileFieldDocument}
        />
        <div>
          {file?.url ? (
            <FileContainer>
              {handleRenderImage()}
              <DeleteButton>
                <AlignCenter>
                  <IconButton
                    disabled={disabled}
                    onClick={props.handleDeleteImage}
                  >
                    <Tooltip placement="right" title="Remove">
                      <HighlightOffIcon />
                    </Tooltip>
                  </IconButton>
                </AlignCenter>
              </DeleteButton>

              <ReplaceButton>
                <AlignCenter>
                  <IconButton
                    disabled={disabled}
                    onClick={() => fileFieldDocument.current?.click()}
                  >
                    <Tooltip placement="right" title="Replace">
                      <LoopIcon />
                    </Tooltip>
                  </IconButton>
                </AlignCenter>
              </ReplaceButton>
            </FileContainer>
          ) : (
            <div ref={dragField}>
              <label htmlFor={state.id}>
                <UploadWrapper>
                  <AlignContent>
                    <UploadIcon>
                      <AlignCenter>
                        <ImageIcon />
                      </AlignCenter>
                    </UploadIcon>
                    <Typography variant="body1" align="center">
                      {props.description || 'Adjunta un archivo'}
                    </Typography>
                    <Typography display="block" variant="caption" align="center">
                      {props.text}
                    </Typography>
                  </AlignContent>
                </UploadWrapper>
              </label>
            </div>
          )}
        </div>
      </ContainerWrapper>

      {props.error && (
        <FormHelperText sx={{ marginLeft: 12, color: 'error.main' }}>
          {props.helperText || 'Campo obligatorio'}
        </FormHelperText>
      )}
    </>
  )
}

export default FileUploader